import { db } from './config';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  writeBatch,
  QueryDocumentSnapshot,
  DocumentData,
  query,
  QueryConstraint
} from 'firebase/firestore';

// Helper to convert Firestore doc to typed object with id
function docWithId(snap: QueryDocumentSnapshot<DocumentData>): unknown {
  return { id: snap.id, ...snap.data() };
}

/**
 * Firestore rejects `undefined` field values ("Unsupported field value:
 * undefined"), but our forms produce them routinely for empty optional fields —
 * `imageUrl: formImageUrl || undefined` and friends. Normalise at this seam so
 * every caller is covered rather than each form remembering on its own.
 *
 * The two operations need different semantics, which is why this is not just
 * `ignoreUndefinedProperties` on the Firestore instance:
 *
 * - On create, an absent field is what "no value" means — drop the key.
 * - On update, dropping the key would leave the *previous* value in place, so
 *   clearing a field (removing an image, blanking a note) would silently fail.
 *   Emit an explicit delete instead.
 *
 * Only `undefined` is special-cased. `0`, `''`, `false` and `null` are all
 * valid Firestore values and are written through untouched.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function stripUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;

  if (Array.isArray(value)) {
    return value
      .map(stripUndefinedDeep)
      .filter(item => item !== undefined);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([key, entryValue]) => [key, stripUndefinedDeep(entryValue)])
    );
  }

  return value;
}

function stripUndefined<T extends object>(data: T): Record<string, unknown> {
  return stripUndefinedDeep(data) as Record<string, unknown>;
}

function undefinedToDeleteField<T extends object>(data: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      value === undefined ? deleteField() : stripUndefinedDeep(value)
    ])
  );
}

// Generic fetch all
export async function fetchCollection<T>(colName: string, queryConstraints?: QueryConstraint[]): Promise<T[]> {
  const colRef = collection(db, colName);
  const q = queryConstraints && queryConstraints.length > 0 ? query(colRef, ...queryConstraints) : colRef;
  const snap = await getDocs(q);
  return snap.docs.map(docWithId) as T[];
}

// Generic add
export async function addToCollection<T>(colName: string, data: Omit<T, 'id'>): Promise<string> {
  const colRef = collection(db, colName);
  const docRef = await addDoc(colRef, stripUndefined(data as object));
  return docRef.id;
}

// Generic update
export async function updateInCollection<T>(colName: string, id: string, data: Partial<T>): Promise<void> {
  const docRef = doc(db, colName, id);
  await updateDoc(docRef, undefinedToDeleteField(data as object));
}

// Generic set by id
export async function setInCollection<T>(colName: string, id: string, data: Partial<T>): Promise<void> {
  const docRef = doc(db, colName, id);
  await setDoc(docRef, stripUndefined(data as object), { merge: true });
}

// Generic delete
export async function deleteFromCollection(colName: string, id: string): Promise<void> {
  const docRef = doc(db, colName, id);
  await deleteDoc(docRef);
}

// Generic get by id
export async function getFromCollection<T>(colName: string, id: string): Promise<T | null> {
  const docRef = doc(db, colName, id);
  const snap = await getDoc(docRef);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as T) : null;
}

// Batch update multiple documents in a single atomic write.
export async function batchUpdateFields(
  colName: string,
  updates: Array<{ id: string; fields: Record<string, unknown> }>
): Promise<void> {
  const batch = writeBatch(db);
  for (const { id, fields } of updates) {
    batch.update(doc(db, colName, id), fields);
  }
  await batch.commit();
}

// Collection names
export const COLLECTIONS = {
  EVENTS: 'samadhi_events',
  ADMINS: 'samadhi_admins',
  DONATION_ITEMS: 'samadhi_donation_items',
  DONATION_REQUESTS: 'samadhi_donation_requests',
  CONTACTS: 'samadhi_contacts',
  BOOKINGS: 'samadhi_bookings',
  // Monk Accommodation Management
  MONK_VISITS: 'samadhi_monk_visits',
  ACCOMMODATION_LOCATIONS: 'samadhi_accommodation_locations',
  VEHICLE_GROUPS: 'samadhi_vehicle_groups',
  // VAS-01: Robe rotation reuses the existing `samadhi_events` collection
  // (slots stored as events with eventType='robe-rotation') so it inherits
  // the booking, waitlist, and email-trigger infrastructure plus YearContext.
  // ROBE_SLOTS is therefore an alias of EVENTS, not a new collection.
  ROBE_SLOTS: 'samadhi_events',
  // VAS-01: Maintenance task tracker (its own collection).
  MAINTENANCE_TASKS: 'samadhi_maintenance_tasks',
  // Admin-managed event categories (global list, public read / admin write).
  EVENT_CATEGORIES: 'samadhi_event_categories',
  // Vas Programme teams + budget (year-scoped, admin only).
  VAS_TEAMS: 'samadhi_vas_teams',
  VAS_BUDGET_LINES: 'samadhi_vas_budget_lines',
  ROLE_PERMISSIONS: 'samadhi_role_permissions',
  ACTION_TYPES: 'samadhi_action_types',
};
